<template>
  <component
    :is="dndComponent"
    ref="dndel"
    v-show="!element.isDeleting"
    :id="element.elType === BaseItemTypes.FOLDER ? element.FolderId : element.Id"
    :index="inputIndex"
    :highlight-condition="allowHighlightOnHover"
    :hover-condition="noop"
    :CtcDropCondition="allowChildToChildDropAction"
    :CtrDropCondition="allowChildToRootDropAction"
    :RtrDropCondition="allowHoverAction"
    :RtcDropCondition="allowRootToChildDropAction"
    :type="dragType"
    :into-allowed="intoAllowedComputed"
    :max-height="28"
    :callback-params="callbackParams"
    :element-name="element.Name"
    :can-drag-check="canDragCheck"
    :between-root-drop-via-indicator="!isReportTreeNode && betweenRootDropViaIndicator"
    :can-drop-to-root-check="!isReportTreeNode ? canDropToRootForDrag : undefined"
    @changeParent="(p: any) => $emit('changeParent', p)"
    @changePosition="(p: any) => $emit('changePosition', p)"
    @cardMoved="(p: any) => $emit('cardMoved', p)"
    :pointer-mode="true"
  >
    <div
      :class="['ble-lite', { 'ble-lite--selected': element.isSelected, 'ble-lite--link-highlight': isLinkHighlighted, 'ble-lite--expanded': effectiveExpanded && hasChildren, 'ble-lite--menu-open': menuOpen, 'ble-lite--actions-visible': showHoverActions, 'ble-lite--blocked': isTemporarilyBlocked }]"
      :style="rowStyle"
      @mouseenter="onRowEnter"
      @mouseleave="onRowLeave"
    >
      <div class="ble-lite__row" @click="selectItem" @mousedown="onRowMouseDown">
        <mosta-checkbox
          v-if="selectionMode && !isReportTreeNode"
          class="ble-lite__selection-check"
          :model-value="isChecked"
          @update:modelValue="$emit('toggleItemSelection', callbackParams)"
          @click.stop
        />
        <span
          v-if="hasChildren"
          class="ble-lite__chevron"
          :class="{ 'ble-lite__chevron--open': effectiveExpanded }"
          @click.stop="$emit('toggleExpand', element)"
        >
          <svg class="ble-lite__chevron-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <q-tooltip
            :delay="300"
            anchor="top middle"
            self="center middle"
            class="ble-lite__chevron-tooltip-popup"
          >
            {{ $t(effectiveExpanded ? 'collapse_list' : 'expand_list') }}
          </q-tooltip>
        </span>
        <span
          v-else
          :class="chevronPlaceholderClass"
        ></span>

        <img
          v-if="isFolder"
          class="ble-lite__type-icon ble-lite__type-icon--folder"
          :src="folderIcon"
          @click.stop="triggerAddChildFolderFromIcon"
          @mouseenter="isFolderIconHovered = true"
          @mouseleave="isFolderIconHovered = false"
        />
        <img v-else-if="element.elType === BaseItemTypes.LIST" class="ble-lite__type-icon ble-lite__type-icon--list" :src="listIconSvg" />
        <img v-else-if="element.elType === BaseItemTypes.PIE_CHART" class="ble-lite__type-icon" :src="pieChartIconSvg" />
        <img v-else-if="element.elType === BaseItemTypes.BAR_CHART" class="ble-lite__type-icon" :src="barChartIconSvg" />
        <img v-else-if="element.elType === BaseItemTypes.GRAPH" class="ble-lite__type-icon ble-lite__type-icon--line" :src="lineChartIconSvg" />
        <i v-else-if="typeIcon" class="material-icons ble-lite__type-icon">{{ typeIcon }}</i>

        <div class="ble-lite__name">
          <span
            class="ble-lite__name-flow"
            :class="{ 'ble-lite__name-flow--editing': element.isEditing }"
          >
            <native-input
              ref="nameInput"
              class="ble-lite__name-edit"
              :model-value="element.Name"
              :allow-line-break="false"
              :disabled-field="!element.isEditing"
              :highlight-tokens="searchTokens"
              :edit-underline="true"
              :inline-icons="true"
              :block-empty-submit="true"
              :external-warning="isDuplicateNameWarning"
              :external-warning-text="$t('rename_duplicate_name_exists')"
              :revert-on-escape="true"
              :clear-hotkey="true"
              @update:modelValue="onNameUpdated"
              @liveValue="onNameLiveValue"
              @cancel="onEditCancel"
              @blur="onEditBlur"
            >
              <template #inline-suffix>
                <span v-if="projLink && !element.isEditing" class="ble-lite__proj-link" @click.stop="$emit('switchProjection', { element, isRoot })">{{ projLink }}</span>
                <!-- Хвост с карандашом: U+2060 WORD JOINER запрещает перенос перед иконкой,
                     а nowrap на обёртке не даёт разорвать связку joiner-иконка. Только
                     joiner не спасает: через границу элементов браузер всё равно
                     переносит иконку, и она остаётся одна на новой строке. -->
                <span v-if="!isTemporarilyBlocked && !element.isEditing" class="ble-lite__edit-btn-tail">&#8288;<span
                    class="ble-lite__edit-btn"
                    @mouseenter="isEditIconHovered = true"
                    @mouseleave="isEditIconHovered = false"
                    @click.stop="startEdit"
                  >
                    <img
                      :src="editIconForDisplay"
                      class="ble-lite__edit-btn-icon"
                      draggable="false"
                    />
                    <q-tooltip :delay="300" anchor="top middle" self="center middle">
                      {{ $t('rename') }}
                    </q-tooltip>
                  </span></span>
              </template>
            </native-input>
          </span>
        </div>

        <span
          v-if="!element.isEditing && !isTemporarilyBlocked && element.IsFavorite && !isReportNode"
          class="ble-lite__heart-btn"
          @mouseenter="isHeartHovered = true"
          @mouseleave="isHeartHovered = false"
          @click.stop="removeFromFavorites($event)"
        >
          <img
            :src="isHeartHovered ? heartSvg : heartActiveSvg"
            class="ble-lite__heart-indicator"
            draggable="false"
          />
          <q-tooltip :delay="300" anchor="top middle" self="center middle">
            {{ $t('remove_from_favorites') }}
          </q-tooltip>
        </span>
        <div v-if="!element.isEditing && !isTemporarilyBlocked" class="ble-lite__right">
          <i
            v-if="canAddBreak"
            class="material-icons ble-lite__act ble-lite__act-break"
            :title="$t('add_break')"
            @click.stop="addBreakAfter"
          >height</i>
          <img
            :src="moreVertIconForDisplay"
            class="ble-lite__act ble-lite__act-menu"
            ref="menuAnchor"
            @mouseenter="onMenuIconEnter"
            @mouseleave="onMenuIconLeave"
            @click.stop="toggleMenu"
          />
        </div>

      </div>
    </div>

    <mosta-context-menu
      :visible="menuOpen"
      :position="menuPos"
      :min-width="200"
      :disable-backdrop-pointer="true"
      menu-class="ble-lite-menu"
      @close="menuOpen = false"
      @menuEnter="onElementMenuEnter"
      @menuLeave="onElementMenuLeave"
    >
          <div
            v-if="showActionsSection"
            class="ble-lite-menu__item ble-lite-menu__item--sub"
            @mouseenter="onActionsSubTriggerEnter"
            @mouseleave="onActionsSubTriggerLeave"
          >
            <ContextMenuIcon :src="ContextMenuIcons.Actions" />
            {{ $t('menu_actions') }}
            <svg
              class="ble-lite-menu__sub-arrow"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M6 12L10 8L6 4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div
              v-if="actionsSubOpen"
              class="ble-lite-menu__submenu"
              @mouseenter="onActionsSubMenuEnter"
              @mouseleave="onActionsSubMenuLeave"
            >
              <div
                v-if="showAddChildSelection"
                class="ble-lite-menu__item"
                @click="menuAction('addChildSelection')"
              >
                <ContextMenuIcon :src="childSelectionMenuIcon(element.elType)" />
                {{ element.elType === 7 ? $t('create_selection') : $t('create_child_selection') }}
              </div>

              <div v-if="showAddChildSelection && showChartsSection" class="ble-lite-menu__sep"></div>
              <div v-if="showChartsSection" class="ble-lite-menu__item" @click="menuChart(2)">
                <ContextMenuIcon :src="ContextMenuIcons.LinearChart" />
                {{ $t('add_linear_chart') }}
              </div>
              <div v-if="showChartsSection" class="ble-lite-menu__item" @click="menuChart(3)">
                <ContextMenuIcon :src="ContextMenuIcons.PieChart" />
                {{ $t('add_pie_chart') }}
              </div>
              <div v-if="showChartsSection" class="ble-lite-menu__item" @click="menuChart(4)">
                <ContextMenuIcon :src="ContextMenuIcons.BarChart" />
                {{ $t('add_bar_chart') }}
              </div>
              <!-- Разделитель после графиков рисуем только если ниже есть пункт (создание папки/клонирование), иначе будет двойной/висящий разделитель. -->
              <div v-if="showChartsSection && (element.elType === 7 || ![901].includes(element.elType))" class="ble-lite-menu__sep"></div>

              <div
                v-if="element.elType === 7"
                class="ble-lite-menu__item"
                @click="menuAction('addChildFolder')"
              >
                <ContextMenuIcon :src="ContextMenuIcons.Folder" />
                {{ $t('create_folder') }}
              </div>
              <div
                v-if="![901].includes(element.elType)"
                class="ble-lite-menu__item"
                @click="menuAction('clone')"
              >
                <ContextMenuIcon :src="ContextMenuIcons.Clone" />
                {{ $t('clone') }}
              </div>

              <div v-if="showProjectionSection" class="ble-lite-menu__sep"></div>

              <div
                v-if="showProjectionSection"
                class="ble-lite-menu__item ble-lite-menu__item--sub"
                @mouseenter="onProjSubTriggerEnter"
                @mouseleave="onProjSubTriggerLeave"
              >
                <ContextMenuIcon :src="ContextMenuIcons.Projection" />
                {{ $t('add_projection') }}
                <svg
                  class="ble-lite-menu__sub-arrow"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div
                  v-if="projSubOpen"
                  class="ble-lite-menu__submenu"
                  @mouseenter="onProjSubMenuEnter"
                  @mouseleave="onProjSubMenuLeave"
                >
                  <template v-for="(item, idx) in projectionSubmenuItems" :key="item.prType">
                    <div v-if="idx > 0" class="ble-lite-menu__sep"></div>
                    <div class="ble-lite-menu__item" @click="menuProjection(item.prType)">
                      <ContextMenuIcon :src="PROJECTION_SUBMENU_ICONS[item.prType]" />
                      {{ $t(item.labelKey) }}
                    </div>
                  </template>
                </div>
              </div>

              <div
                v-if="[0,1,6].includes(element.elType) && [0,1,2,3,4,5,7,8].includes(element.DestType)"
                class="ble-lite-menu__item"
                @click="menuAction('addChildList')"
              >
                <ContextMenuIcon :src="ContextMenuIcons.List" />
                {{ $t('create_list') }}
              </div>

              <div v-if="element.elType !== 7" class="ble-lite-menu__sep"></div>
              <div v-if="element.elType !== 7" class="ble-lite-menu__item" @click="menuAction('addToReport')">
                <ContextMenuIcon :src="ContextMenuIcons.AddToReport" />
                {{ $t('add_to_report') }}
              </div>
            </div>
          </div>

          <div v-if="canToggleElementSelection" class="ble-lite-menu__sep"></div>
          <div
            v-if="canToggleElementSelection"
            class="ble-lite-menu__item"
            @click="menuAction('selectElement')"
          >
            <ContextMenuIcon :src="ContextMenuIcons.SelectElement" />
            {{ $t('menu_select_element') }}
          </div>

          <div v-if="showFavorite" class="ble-lite-menu__sep"></div>
          <div v-if="showFavorite" class="ble-lite-menu__item" @click="menuAction('favorite')">
            <ContextMenuIcon :src="element.IsFavorite ? ContextMenuIcons.HeartActive : ContextMenuIcons.Heart" />
            {{ element.IsFavorite ? $t('remove_from_favorites') : $t('add_to_favorites') }}
          </div>

          <div class="ble-lite-menu__sep"></div>
          <div class="ble-lite-menu__item" @click="menuAction('copyLink', $event)">
            <ContextMenuIcon :src="ContextMenuIcons.CopyLink" />
            {{ $t('copy_link') }}
          </div>

          <div class="ble-lite-menu__sep"></div>
          <div class="ble-lite-menu__item" @click="menuAction('info')">
            <ContextMenuIcon :src="ContextMenuIcons.Info" />
            {{ $t('menu_view_information') }}
          </div>

          <div class="ble-lite-menu__sep"></div>
          <div class="ble-lite-menu__item" @click="menuAction('delete')">
            <ContextMenuIcon :src="ContextMenuIcons.Delete" />
            {{ $t('remove') }}
          </div>
    </mosta-context-menu>
  </component>

  <!--
    Подсказка «Ссылка скопирована в буфер обмена» после copyLink.
    Невидимый фиксированный anchor в координатах клика + штатный q-tooltip
    (стиль по умолчанию совпадает с тултипом «Обновить» в Analytics.vue).
  -->
  <Teleport to="body">
    <div
      v-if="copyTipShown"
      :style="{ position: 'fixed', top: copyTipPos.y + 'px', left: copyTipPos.x + 'px', width: '0', height: '0', pointerEvents: 'none', zIndex: 9999 }"
    >
      <q-tooltip
        v-model="copyTipShown"
        no-parent-event
        :offset="[0, 8]"
        anchor="bottom middle"
        self="top middle"
      >
        {{ $t('link_copied_to_clipboard') }}
      </q-tooltip>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, reactive, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
// @ts-ignore
import DndListElement from '@/components/dnd/DndListElement.vue';
// @ts-ignore
import DndReorderElement from '@/components/dnd/DndReorderElement.vue';
import NativeInput from '@/components/common/basic/NativeInput.vue';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import { BaseListItem } from './interfaces/ListTypes';
import { BaseItemTypes, DragItemTypes } from '@/components/dnd/ItemTypes';
import { trimEllip } from '@/models/helpers/recursive';
import { buildAbsoluteClientUrl } from '@/utils/urlHelpers';
import folderClosedSvg from '@/assets/folder-closed.svg';
import folderOpenSvg from '@/assets/folder-open.svg';
import folderClosedPlusSvg from '@/assets/folder-closed-plus.svg';
import folderOpenPlusSvg from '@/assets/folder-open-plus.svg';
import folderClosedPlusHoverSvg from '@/assets/folder-closed-plus-hover.svg';
import folderOpenPlusHoverSvg from '@/assets/folder-open-plus-hover.svg';

// Предзагрузка hover-иконок папок, чтобы при ховере не было сетевых запросов
[folderClosedPlusSvg, folderOpenPlusSvg, folderClosedPlusHoverSvg, folderOpenPlusHoverSvg].forEach(src => {
  const img = new Image();
  img.src = src;
});

import heartSvg from '@/assets/heart.svg';
import heartActiveSvg from '@/assets/heart-active.svg';
import listIconSvg from '@/assets/list-icon.svg';
import pieChartIconSvg from '@/assets/pie-chart-icon.svg';
import barChartIconSvg from '@/assets/bar-chart-icon.svg';
import lineChartIconSvg from '@/assets/line-chart-icon.svg';
import moreVertIconSvg from '@/assets/more-vert-icon.svg';
import moreVertIconHoverSvg from '@/assets/more-vert-icon-hover.svg';
import editIconSvg from '@/assets/edit-icon.svg';
import editIconHoverSvg from '@/assets/edit-icon-hover.svg';
import { getAnalyticsV2ElementTypeName } from '@/models/helpers/type-names';
import { useQuasar } from 'quasar';
import ElementInfoDialog from './ElementInfoDialog.vue';
import MostaContextMenu from '@/components/common/basic/MostaContextMenu.vue';
import ContextMenuIcon from '@/components/common/context-menu/ContextMenuIcon.vue';
import { ContextMenuIcons, PROJECTION_SUBMENU_ICONS, childSelectionMenuIcon } from '@/components/common/context-menu/contextMenuIcons';
import { markRenameEnded, consumeRenameHandoff } from './renameHandoff';
import { generalStore } from '@/store/general';

const genstore = generalStore();


const props = defineProps<{
  element: BaseListItem;
  depth: number;
  isRoot: boolean;
  hasChildren: boolean;
  effectiveExpanded?: boolean;
  inputIndex: number;
  parentId?: number;
  parentType?: BaseItemTypes;
  /** true или функция (dragParams, hoverParams) => true если дроп в этот элемент запрещён (папка в себя/вложенную). */
  intoForbidden?: boolean | ((dragParams: any, hoverParams: any) => boolean);
  scrollViewport?: any;
  allowHighlightOnHover: Function;
  allowHoverAction: Function;
  allowChildToChildDropAction: Function;
  allowChildToRootDropAction: Function;
  allowRootToChildDropAction?: Function;
  selectionMode?: boolean;
  isChecked?: boolean;
  /** Соседи текущего элемента на том же уровне дерева (для проверки дубликатов при rename). */
  renameSiblings?: BaseListItem[];
  /** true если элемент выбран не напрямую, а унаследованно от родителя (для отладки — голубой чекбокс). */
  isInherited?: boolean;
  /** Временный lock создания дочерней папки для этого родителя (до ответа API). */
  isCreateFolderPendingForThisParent?: boolean;
  /** Временный lock перемещения для этого элемента (source/target) до ответа API. */
  isMovePendingForThisItem?: boolean;
  /** true если элемент находится в дереве отчёта (сам REPORT-корень или любой его потомок). */
  inReportContext?: boolean;
  /** Опция левого списка: полоска между элементами = drop в корень. */
  betweenRootDropViaIndicator?: boolean;
  /** Проверка canDropToRoot для between→root (с учётом мультивыбора). */
  canDropToRootForDrag?: (dragEl: any) => boolean;
}>();

const $q = useQuasar();
const emit = defineEmits<{
  (e: 'toggleExpand', el: BaseListItem): void;
  (e: 'cardSelected', arg: any): void;
  (e: 'cardDeleted', el: BaseListItem, anchorPos: { x: number; y: number }): void;
  (e: 'cardFavorite', arg: any): void;
  (e: 'cardRenamed', arg: any): void;
  (e: 'cardCloned', el: BaseListItem): void;
  (e: 'switchProjection', arg: { element: BaseListItem; isRoot: boolean }): void;
  (e: 'changeParent', arg: any): void;
  (e: 'changePosition', arg: any): void;
  (e: 'cardMoved', arg: any): void;
  (e: 'breakAfter', arg: any): void;
  (e: 'addChildSelection', arg: any): void;
  (e: 'addChildProjection', arg: any): void;
  (e: 'addChildList', arg: any): void;
  (e: 'addChildChart', arg: any): void;
  (e: 'addChildFolder', arg: any): void;
  (e: 'addToReport', el: BaseListItem): void;
  (e: 'enterSelectionMode', params: any): void;
  (e: 'toggleItemSelection', params: any): void;
}>();

const noop = () => {};

/** Узел сегмента «Отчёты»: либо сам REPORT-корень, либо любой его потомок (определяется родителем-отчётом). */
const isReportNode = computed(() => props.element.elType === BaseItemTypes.REPORT);

/**
 * Одноразовая подсветка элемента, на который перешли по ссылке (GET-параметры).
 * Строгое сравнение Identity: для папок — elType + FolderId, для остальных — elType + Id + SrcType + DestType.
 * Гасится первым кликом мыши (clearLinkHighlight в VirtualAnalyticsList).
 */
const isLinkHighlighted = computed(() => {
  const hl: any = genstore.linkHighlight;
  if (!hl) return false;
  const el: any = props.element;
  if (hl.elType === BaseItemTypes.FOLDER) {
    return el.elType === BaseItemTypes.FOLDER && el.FolderId === hl.FolderId;
  }
  return el.elType === hl.elType &&
    el.Id === hl.Id &&
    (hl.SrcType ? el.SrcType === hl.SrcType : !el.SrcType) &&
    el.DestType === hl.DestType;
});
/** В отчётном дереве (REPORT-корень + любые потомки): прокидывается из VirtualAnalyticsList. */
const isReportTreeNode = computed(() => !!props.inReportContext);
/** Разрешение «вложить»: выборка/список/папка всегда принимают перенос; остальные — только без ElementId и не графики. */
const baseIntoAllowed = computed(() => {
  // В отчётном дереве «вкладывать» нельзя: REPORT-корень и его дети — только sibling-reorder.
  if (isReportTreeNode.value) return false;
  return [BaseItemTypes.SELECTION, BaseItemTypes.LIST, BaseItemTypes.FOLDER].includes(props.element.elType) ||
    (!props.element.ElementId && ![1, 2, 3, 4].includes(props.element.elType));
});
/** Для DnD: если intoForbidden — функция (проверка папка в себя/вложенную), передаём функцию в dnd-list-element.
 *  Для отчётного дерева используется DndReorderElement, который ждёт строго boolean — поэтому для report-узлов
 *  всегда возвращаем false и не оборачиваем в функцию. */
const intoAllowedComputed = computed(() => {
  const base = baseIntoAllowed.value;
  if (isReportTreeNode.value) return false;
  if (typeof props.intoForbidden === 'function') {
    // Для корневых папок (CARD_ROOT) не применяем intoForbidden при дропе root→root: там потомков быть не может,
    // и запрет «в себя» обрабатывается отдельно через allowRootToRootDropAction.
    return (dragParams: any, hoverParams: any) => {
      return base && !(props.intoForbidden as (d: any, h: any) => boolean)(dragParams, hoverParams);
    };
  }
  return base && !props.intoForbidden;
});

/**
 * Какой DnD-компонент использовать:
 * - В отчётном дереве — DndReorderElement (sibling-reorder с индикатором между элементами);
 * - В остальных сегментах — DndListElement (нынешнее поведение: смена родителя «во вложение»).
 */
const dndComponent = computed(() => isReportTreeNode.value ? DndReorderElement : DndListElement);

/**
 * Подменю «Действия» в контекстном меню. В отчётном дереве оно скрыто:
 * операции «Создать вложенную выборку / Клонировать / Добавить проекцию / Создать список /
 * Добавить графики / Добавить в отчёт» к узлам отчёта неприменимы.
 */
const showActionsSection = computed(() => !isReportTreeNode.value);

/**
 * Hover-иконка «Добавить разрыв». Доступна только для табличных элементов внутри отчёта
 * (у REPORT-корня и у разрывов разделителя — нет, у них нет ElementId).
 */
const canAddBreak = computed(() => isReportTreeNode.value && !isReportNode.value && !!(props.element as any).ElementId);
const nameInput = ref<InstanceType<typeof NativeInput> | null>(null);
const menuAnchor = ref<HTMLElement | null>(null);
const menuOpen = ref(false);
const menuPos = ref({ top: '0px', left: '0px' });
const actionsSubOpen = ref(false);
const projSubOpen = ref(false);
const EMPTY_TOKENS: string[] = [];

const showHoverActions = ref(false);
const isRowHovered = ref(false);
const isFolderIconHovered = ref(false);
const isEditIconHovered = ref(false);
const isMenuIconHovered = ref(false);
const isHeartHovered = ref(false);
let hoverTimer: ReturnType<typeof setTimeout> | null = null;
let menuCloseTimer: ReturnType<typeof setTimeout> | null = null;
let actionsSubCloseTimer: ReturnType<typeof setTimeout> | null = null;
let projSubCloseTimer: ReturnType<typeof setTimeout> | null = null;

// Подсказка «Ссылка скопирована в буфер обмена» после copyLink:
// показываем штатный q-tooltip, привязанный к невидимому фиксированному
// anchor-блоку в координатах клика, и сами скрываем через 2с.
const copyTipShown = ref(false);
const copyTipPos = reactive({ x: 0, y: 0 });
let copyTipTimer: ReturnType<typeof setTimeout> | null = null;

function onRowEnter() {
  isRowHovered.value = true;
  if (hoverTimer) clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => { showHoverActions.value = true; }, 400);
}

function onRowLeave() {
  isRowHovered.value = false;
  isEditIconHovered.value = false;
  if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
  showHoverActions.value = false;
}

onBeforeUnmount(() => {
  if (hoverTimer) clearTimeout(hoverTimer);
  if (menuCloseTimer) clearTimeout(menuCloseTimer);
  if (actionsSubCloseTimer) clearTimeout(actionsSubCloseTimer);
  if (projSubCloseTimer) clearTimeout(projSubCloseTimer);
  if (copyTipTimer) clearTimeout(copyTipTimer);
  document.removeEventListener('mousedown', onDocumentMousedown, true);
});

onMounted(() => {
  document.addEventListener('mousedown', onDocumentMousedown, true);
});

/** Тип DnD: для выборок различаем корневые/дочерние (CARD_ROOT/CARD_CHILD), для папок достаточно одного типа CARD_CHILD (правила для папок не зависят от корневости). */
const dragType = computed(() =>
  props.isRoot && props.element.elType !== BaseItemTypes.FOLDER ? 'card_root' : 'card_child'
);

/** Класс-placeholder под стрелку: полную ширину под шеврон резервируем у папок, корневых элементов и у всего, что лежит прямо в папке — тогда тексты выборок/списков/графиков встают ровно под вложенной папкой того же уровня. Для элементов, вложенных в другие элементы (выборку/список/проекцию), оставляем компактный отступ. */
const chevronPlaceholderClass = computed(() => {
  const isFolderType = props.element.elType === BaseItemTypes.FOLDER;
  const isRootCard = props.isRoot;
  const parentIsFolder = props.parentType === BaseItemTypes.FOLDER;
  return isFolderType || isRootCard || parentIsFolder ? 'ble-lite__no-chevron--full' : 'ble-lite__no-chevron';
});

/** Эффективное состояние раскрытия: используем рассчитанное в VirtualAnalyticsList (поиск/избранное), иначе fallback к локальному search-auto-expand. */
const effectiveExpanded = computed(() =>
  props.effectiveExpanded ?? (!!props.element.isExpanded || (props.element as any)._searchAutoExpand === true)
);

/** Параметры для DnD и moveItemToParent. isRoot — буквально корневой (без родителя); «корневая выборка по смыслу» считается в allowRootToRootDropAction.
 * ВАЖНО: Id и FolderId передаём РАЗДЕЛЬНО (id = Id, folderId = FolderId), чтобы для сравнения использовать ту же самую Identity, что и при выборе.
 */
const callbackParams = computed(() => ({
  id: props.element.Id,
  folderId: props.element.FolderId,
  parent_id: props.parentId,
  parent_type: props.parentType,
  isRoot: props.isRoot,
  elType: props.element.elType,
  sourceDataType: props.element.SrcType,
  isEditing: props.element.isEditing,
  isProcessing: isTemporarilyBlocked.value,
  isExpanded: props.element.isExpanded,
  destinationDataType: props.element.DestType,
  innerElementId: props.element.ElementId,
  CombinedName: props.element.CombinedName,
  Name: props.element.Name,
  viewport: props.scrollViewport,
}));

const isTemporarilyBlocked = computed(() =>
  !!props.isCreateFolderPendingForThisParent
  || !!props.isMovePendingForThisItem
  || !!(props.element as any).isLoadingChildren
);

const canDragCheck = () =>
  !isTemporarilyBlocked.value &&
  !props.element.isEditing &&
  (!props.selectionMode || !!props.isChecked);

const rowStyle = computed(() => ({ paddingLeft: (props.depth * 22) + 'px' }));

const isFolder = computed(() => props.element.elType === BaseItemTypes.FOLDER);

const folderIcon = computed(() => {
  if (isFolderIconHovered.value) {
    return effectiveExpanded.value ? folderOpenPlusHoverSvg : folderClosedPlusHoverSvg;
  }
  if (isRowHovered.value) {
    return effectiveExpanded.value ? folderOpenPlusSvg : folderClosedPlusSvg;
  }
  return effectiveExpanded.value ? folderOpenSvg : folderClosedSvg;
});

const typeIcon = computed(() => {
  switch (props.element.elType) {
    case BaseItemTypes.FOLDER: return '';
    case BaseItemTypes.LIST: return '';
    case BaseItemTypes.GRAPH: return '';
    case BaseItemTypes.PIE_CHART: return '';
    case BaseItemTypes.BAR_CHART: return '';
    case BaseItemTypes.BREAK: return 'height';
    default: return '';
  }
});

const editIconForDisplay = computed(() =>
  isEditIconHovered.value ? editIconHoverSvg : editIconSvg
);

const moreVertIconForDisplay = computed(() =>
  isMenuIconHovered.value ? moreVertIconHoverSvg : moreVertIconSvg
);

const searchTokens = computed(() => (props.element as any)._searchTokens || EMPTY_TOKENS);
const editDraftName = ref(props.element.Name || '');

function isSameIdentity(left: BaseListItem, right: BaseListItem): boolean {
  if (!left || !right) return false;
  if (left.elType === BaseItemTypes.FOLDER && right.elType === BaseItemTypes.FOLDER) {
    return left.FolderId === right.FolderId;
  }
  return left.elType === right.elType &&
    left.Id === right.Id &&
    ((right.SrcType !== undefined && right.SrcType !== null) ? (left.SrcType === right.SrcType) : !left.SrcType) &&
    left.DestType === right.DestType;
}

function hasDuplicateOnLevel(name: string): boolean {
  const siblings = props.renameSiblings || [];
  return siblings.some((candidate) =>
    candidate &&
    candidate.elType === props.element.elType &&
    candidate.Name === name &&
    !isSameIdentity(candidate, props.element),
  );
}

const isDuplicateNameWarning = computed(() => {
  if (!props.element.isEditing) return false;
  if (!editDraftName.value) return false;
  return hasDuplicateOnLevel(editDraftName.value);
});

const projLink = computed(() => {
  const el = props.element;
  if (el.elType && el.elType <= 1) {
    return ` (${getAnalyticsV2ElementTypeName(el.SrcType)} → ${getAnalyticsV2ElementTypeName(el.DestType)})`;
  }
  return '';
});

/** Проекция «корневая по смыслу»: либо буквально корневая, либо лежит в папке (родитель — не выборка). */
const isRootProjectionByMeaning = computed(() =>
  props.element.elType === BaseItemTypes.PROJECTION &&
  (props.isRoot || props.parentType === BaseItemTypes.FOLDER)
);

/** Список «корневой по смыслу»: либо буквально корневой, либо лежит в папке. */
const isRootListByMeaning = computed(() =>
  props.element.elType === BaseItemTypes.LIST &&
  (props.isRoot || props.parentType === BaseItemTypes.FOLDER)
);

/** Условие показа пункта «Создать (вложенную) выборку» — используется и для пункта, и для защиты разделителя перед графиками. */
const showAddChildSelection = computed(() =>
  [0, 6, 7].includes(props.element.elType) ||
  (props.element.elType === 1 && (props.isRoot || props.parentType === BaseItemTypes.FOLDER))
);

const showProjectionSection = computed(() =>
  props.element.DestType !== 5 && props.element.DestType !== 6 &&
  ([0, 6].includes(props.element.elType) || isRootProjectionByMeaning.value)
);

/** Пункты подменю «Добавить проекцию»: фиксированный порядок, те же условия видимости по DestType. */
const projectionSubmenuItems = computed(() => {
  const destType = props.element.DestType;
  const defs: { prType: number; labelKey: string; destTypes: number[] }[] = [
    { prType: 1, labelKey: 'submenu_to_clients', destTypes: [0, 2, 3, 4] },
    { prType: 0, labelKey: 'submenu_to_projects', destTypes: [1, 2, 3, 4] },
    { prType: 2, labelKey: 'submenu_to_jobs', destTypes: [3] },
    { prType: 4, labelKey: 'submenu_to_files', destTypes: [0, 1] },
    { prType: 8, labelKey: 'submenu_to_movements', destTypes: [7] },
    { prType: 7, labelKey: 'submenu_to_positions', destTypes: [8] },
  ];
  return defs.filter((d) => d.destTypes.includes(destType));
});

const showChartsSection = computed(() => {
  if (props.element.DestType === 5) return false; // на сегменте тегов графики скрываем

  const elType = props.element.elType;
  const isRootSelectionByMeaning =
    elType === BaseItemTypes.SELECTION &&
    (props.isRoot || props.parentType === BaseItemTypes.FOLDER);

  // Графики можно добавлять:
  // - у буквальных корневых выборок/проекций/списков
  // - у «корневых по смыслу» выборок, проекций и списков (когда лежат в папке)
  // - у папок
  return (
    (props.isRoot && [BaseItemTypes.SELECTION, BaseItemTypes.PROJECTION, BaseItemTypes.LIST].includes(elType)) ||
    isRootSelectionByMeaning ||
    isRootProjectionByMeaning.value ||
    isRootListByMeaning.value ||
    elType === BaseItemTypes.FOLDER
  );
});

const showFavorite = computed(() =>
  props.element.elType !== BaseItemTypes.REPORT &&
  props.element.elType !== BaseItemTypes.BREAK
);

// В режиме отчётов (сам отчёт или любой его потомок) массовое выделение недоступно.
const canToggleElementSelection = computed(() => !props.selectionMode && !isReportTreeNode.value);

// Точка нажатия, чтобы отличить клик от перетаскивания: после переноса браузер всё
// равно присылает click по строке, и без этой проверки выбор элемента переключался бы
// на каждом drop. Порог тот же, что у touchSlop в опциях бэкенда.
const CLICK_SLOP_PX = 5;
let pressPoint: { x: number; y: number } | null = null;

function movedBeyondClick(event?: MouseEvent): boolean {
  if (!event || !pressPoint) return false;
  const moved = Math.abs(event.clientX - pressPoint.x) > CLICK_SLOP_PX
    || Math.abs(event.clientY - pressPoint.y) > CLICK_SLOP_PX;
  pressPoint = null;
  return moved;
}

// Нажатие по свободному месту своей строки во время правки завершает её: contenteditable
// теряет фокус уже на mousedown, поэтому здесь только гасим последующий click, иначе он
// тут же выбрал бы элемент.
let skipNextRowClick = false;
// Esc не должен оставлять отметку для соседних строк: правку закрыл не клик, и следующий
// клик пользователя обязан работать как обычно.
let renameCancelled = false;

function onRowMouseDown(event: MouseEvent) {
  pressPoint = { x: event.clientX, y: event.clientY };

  if (!props.element.isEditing) return;
  const target = event.target as HTMLElement | null;
  if (!target || typeof target.closest !== 'function') return;
  if (target.closest('.ble-lite__name-edit')) return;
  // Кнопки строки обрабатывают клик сами и через selectItem не проходят, поэтому гасить
  // у них нечего: поднятый флаг остался бы висеть и съел следующий выбор элемента.
  if (target.closest('.ble-lite__selection-check, .ble-lite__chevron, .ble-lite__type-icon, .ble-lite__heart-btn, .ble-lite__right')) {
    return;
  }
  skipNextRowClick = true;
}

function selectItem(event?: MouseEvent) {
  if (skipNextRowClick) {
    skipNextRowClick = false;
    return;
  }
  // Этим кликом пользователь закрыл правку в другой строке, выбирать элемент не нужно.
  if (consumeRenameHandoff()) return;
  if (props.element.isEditing || isTemporarilyBlocked.value) return;
  if (movedBeyondClick(event)) return;
  // В режиме выделения клик по строке переключает галку, не выбирая элемент.
  // Исключение — узлы дерева отчётов: для них массовое выделение не применяется,
  // клик работает как обычный выбор.
  if (props.selectionMode && !isReportTreeNode.value) {
    emit('toggleItemSelection', callbackParams.value);
    return;
  }
  // У папки клик по строке управляет раскрытием, а фокус повторяет его состояние:
  // раскрыли - папка в фокусе, свернули - фокус снят. Считаем по effectiveExpanded,
  // потому что в поиске папка может быть раскрыта авторазворотом, и toggleExpand в
  // этом случае именно сворачивает.
  if (isFolder.value) {
    const willExpand = !effectiveExpanded.value;
    emit('toggleExpand', props.element);
    if (willExpand) {
      if (!props.element.isSelected) {
        emit('cardSelected', { element: props.element, switch: false });
      }
    } else if (props.element.isSelected) {
      emit('cardSelected', { element: props.element, switch: false, clearSelection: true });
    }
    return;
  }
  if (props.element.isSelected) {
    emit('cardSelected', { element: props.element, switch: false, clearSelection: true });
    return;
  }
  emit('cardSelected', { element: props.element, switch: false });
}

// Сердечко в строке снимает отметку избранного без открытия меню. Событие то же, что и у
// пункта меню. Проверка на смещение нужна потому, что после перетаскивания строки браузер
// всё равно присылает click, и элемент вылетал бы из избранного сам по себе.
function removeFromFavorites(event?: MouseEvent) {
  if (props.element.isEditing || isTemporarilyBlocked.value) return;
  if (movedBeyondClick(event)) return;
  isHeartHovered.value = false;
  emit('cardFavorite', { element: props.element });
}

function startEdit() {
  if (!props.element.isEditing) {
    // TODO: isEditing пишется в проп. Перевести на локальное состояние или событие наверх.
    // eslint-disable-next-line vue/no-mutating-props
    props.element.isEditing = true;
    editDraftName.value = props.element.Name || '';
    nextTick(() => nameInput.value?.startEdit());
  }
}

function focusEditFromExternalState() {
  nextTick(() => nameInput.value?.startEdit());
}

onMounted(() => {
  if (props.element.isEditing) {
    focusEditFromExternalState();
  }
});

watch(
  () => props.element.isEditing,
  (isEditing, wasEditing) => {
    if (isEditing && !wasEditing) {
      editDraftName.value = props.element.Name || '';
      focusEditFromExternalState();
      return;
    }
    if (!isEditing && wasEditing) {
      editDraftName.value = props.element.Name || '';
    }
  },
);

function onNameUpdated(newName: string) {
  if (hasDuplicateOnLevel(newName)) {
    return;
  }
  if (newName && newName !== props.element.Name) {
    emit('cardRenamed', { element: props.element, newName });
  }
  // TODO: isEditing пишется в проп. Перевести на локальное состояние или событие наверх.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isEditing = false;
}

function onNameLiveValue(value: string) {
  editDraftName.value = value ?? '';
}

function onEditCancel() {
  renameCancelled = true;
}

// При revert (blur/Esc без коммита) NativeInput не эмитит update:modelValue,
// поэтому isEditing нужно сбросить отдельно по событию blur.
function onEditBlur() {
  // Правку закрыл клик за пределами своей строки: гасим выбор в той строке, куда он попал.
  // Клик по свободному месту своей строки сюда не относится, его ловит skipNextRowClick.
  if (renameCancelled) {
    renameCancelled = false;
  } else if (!skipNextRowClick) {
    markRenameEnded();
  }
  // TODO: isEditing пишется в проп. Перевести на локальное состояние или событие наверх.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isEditing = false;
}

async function copyLink() {
  const el = props.element;
  const linkId = el.elType === BaseItemTypes.FOLDER ? el.FolderId : el.Id;
  if (!linkId) return;
  const url = buildAbsoluteClientUrl(`analytics?type=${el.elType}&src=${el.SrcType === undefined ? '' : el.SrcType}&dest=${el.DestType}&id=${linkId}`);

  let copied = false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      copied = true;
    }
  } catch (_e) {
    // Clipboard API can be blocked by browser permissions policy.
  }

  if (!copied) {
    // Fallback for environments where Clipboard API is unavailable/blocked.
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    copied = true;
  }

  if (copied) {
    showCopyTooltip();
  }
}

// Показываем штатный q-tooltip («Ссылка скопирована в буфер обмена») рядом
// с координатой клика. Через 2с снимаем; повторные клики перезапускают таймер.
function showCopyTooltip() {
  copyTipShown.value = true;
  if (copyTipTimer !== null) {
    clearTimeout(copyTipTimer);
    copyTipTimer = null;
  }
  copyTipTimer = setTimeout(() => {
    copyTipShown.value = false;
    copyTipTimer = null;
  }, 2000);
}

function setMenuPosition(anchor: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const margin = 8;
  const menuGapX = 2;
  // По запросу UI даём небольшой сдвиг вправо относительно кнопки «...».
  const anchorRight = rect.right + menuGapX;
  const anchorTop = rect.top;
  // Первый проход до рендера: ставим возле якоря.
  menuPos.value = {
    top: `${Math.max(margin, anchorTop)}px`,
    left: `${Math.max(margin, Math.min(anchorRight, viewportWidth - 200 - margin))}px`,
  };

  // Второй проход после рендера: учитываем фактический размер меню.
  nextTick(() => {
    const menuEl = document.querySelector('.ble-lite-menu') as HTMLElement | null;
    if (!menuEl) return;
    const menuRect = menuEl.getBoundingClientRect();
    const menuHeight = Math.ceil(menuRect.height);
    const menuWidth = Math.ceil(menuRect.width);

    let top = anchorTop;
    if (top + menuHeight > viewportHeight - margin) {
      top = viewportHeight - menuHeight - margin;
    }
    if (top < margin) top = margin;
    if (top + menuHeight > viewportHeight - margin) top = Math.max(margin, viewportHeight - menuHeight - margin);

    let left = anchorRight;
    if (left + menuWidth > viewportWidth - margin) {
      left = viewportWidth - menuWidth - margin;
    }
    if (left < margin) left = margin;

    menuPos.value = { top: `${top}px`, left: `${left}px` };
  });
}

function clearMenuCloseTimer() {
  if (menuCloseTimer) {
    clearTimeout(menuCloseTimer);
    menuCloseTimer = null;
  }
}

function clearActionsSubCloseTimer() {
  if (actionsSubCloseTimer) {
    clearTimeout(actionsSubCloseTimer);
    actionsSubCloseTimer = null;
  }
}

function clearProjSubCloseTimer() {
  if (projSubCloseTimer) {
    clearTimeout(projSubCloseTimer);
    projSubCloseTimer = null;
  }
}

// Буфер нужен, чтобы курсор успел пройти gap между пунктом и подменю.
function scheduleActionsSubClose() {
  clearActionsSubCloseTimer();
  actionsSubCloseTimer = setTimeout(() => {
    actionsSubOpen.value = false;
    projSubOpen.value = false;
    actionsSubCloseTimer = null;
    clearProjSubCloseTimer();
  }, 180);
}

function scheduleProjSubClose() {
  clearProjSubCloseTimer();
  projSubCloseTimer = setTimeout(() => {
    projSubOpen.value = false;
    projSubCloseTimer = null;
  }, 180);
}

// Небольшой буфер нужен, чтобы курсор можно было перевести с кнопки на меню без закрытия.
function scheduleMenuClose() {
  clearMenuCloseTimer();
  menuCloseTimer = setTimeout(() => {
    menuOpen.value = false;
    actionsSubOpen.value = false;
    projSubOpen.value = false;
    clearActionsSubCloseTimer();
    clearProjSubCloseTimer();
    menuCloseTimer = null;
  }, 220);
}

function openMenu(anchor: HTMLElement) {
  clearMenuCloseTimer();
  setMenuPosition(anchor);
  menuOpen.value = true;
  actionsSubOpen.value = true;
  projSubOpen.value = false;
}

function onMenuIconEnter(ev: MouseEvent) {
  isMenuIconHovered.value = true;
  openMenu(ev.currentTarget as HTMLElement);
}

function onMenuIconLeave() {
  isMenuIconHovered.value = false;
  scheduleMenuClose();
}

function onElementMenuEnter() {
  clearMenuCloseTimer();
}

function onElementMenuLeave() {
  scheduleMenuClose();
}

function onActionsSubTriggerEnter() {
  clearActionsSubCloseTimer();
  actionsSubOpen.value = true;
}

function onActionsSubTriggerLeave() {
  scheduleActionsSubClose();
}

function onActionsSubMenuEnter() {
  clearActionsSubCloseTimer();
  actionsSubOpen.value = true;
}

function onActionsSubMenuLeave() {
  scheduleActionsSubClose();
}

function onProjSubTriggerEnter() {
  clearProjSubCloseTimer();
  projSubOpen.value = true;
}

function onProjSubTriggerLeave() {
  scheduleProjSubClose();
}

function onProjSubMenuEnter() {
  clearProjSubCloseTimer();
  projSubOpen.value = true;
}

function onProjSubMenuLeave() {
  scheduleProjSubClose();
}

function onDocumentMousedown(ev: MouseEvent) {
  if (!menuOpen.value) return;
  const target = ev.target as HTMLElement | null;
  if (!target) return;
  if (menuAnchor.value && menuAnchor.value.contains(target)) return;
  // Клик внутри меню не должен закрывать его до обработки действия пункта.
  if (typeof target.closest === 'function' && target.closest('.ble-lite-menu')) return;
  clearMenuCloseTimer();
  clearActionsSubCloseTimer();
  clearProjSubCloseTimer();
  menuOpen.value = false;
  actionsSubOpen.value = false;
  projSubOpen.value = false;
}

function toggleMenu(ev: MouseEvent) {
  clearMenuCloseTimer();
  const anchor = ev.currentTarget as HTMLElement;
  setMenuPosition(anchor);
  const nextOpen = !menuOpen.value;
  menuOpen.value = nextOpen;
  actionsSubOpen.value = nextOpen;
  projSubOpen.value = false;
}

function hoverParams() {
  return {
    id: props.element.Id,
    folderId: props.element.FolderId,
    isRoot: props.isRoot,
    elType: props.element.elType,
    isEditing: props.element.isEditing,
    isExpanded: props.element.isExpanded,
    sourceDataType: props.element.SrcType,
    destinationDataType: props.element.DestType,
    innerElementId: props.element.ElementId,
    viewport: props.scrollViewport,
    CombinedName: props.element.CombinedName,
  };
}

function triggerAddChildFolderFromIcon() {
  if (props.element.elType !== BaseItemTypes.FOLDER || isTemporarilyBlocked.value) return;
  // TODO: isExpanded пишется в проп. Перевести на событие наверх или на стор дерева.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isExpanded = true;
  emit('addChildFolder', { hoverParams: hoverParams() });
}

function menuAction(action: string, e?: MouseEvent) {
  clearMenuCloseTimer();
  clearActionsSubCloseTimer();
  clearProjSubCloseTimer();
  menuOpen.value = false;
  actionsSubOpen.value = false;
  projSubOpen.value = false;
  if (action === 'selectElement') { emit('enterSelectionMode', callbackParams.value); return; }
  if (action === 'copyLink') {
    // Запоминаем координаты клика, чтобы показать tooltip ровно у пункта меню
    // (после закрытия меню сам пункт уже не существует, поэтому используем фикс. anchor).
    if (e && typeof e.clientX === 'number' && typeof e.clientY === 'number') {
      copyTipPos.x = e.clientX;
      copyTipPos.y = e.clientY;
    }
    copyLink();
    return;
  }
  if (action === 'delete') {
    const rect = menuAnchor.value?.getBoundingClientRect();
    const pos = rect ? { x: rect.left - 115, y: rect.top } : { x: 0, y: 0 };
    emit('cardDeleted', props.element, pos);
    return;
  }
  if (action === 'favorite') { emit('cardFavorite', { element: props.element }); return; }
  if (action === 'info') {
    $q.dialog({
      component: ElementInfoDialog,
      componentProps: { element: props.element },
    });
    return;
  }
  // TODO: isExpanded пишется в проп. Перевести на событие наверх или на стор дерева.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isExpanded = true;
  const hp = hoverParams();
  switch (action) {
    case 'addChildSelection': emit('addChildSelection', { hoverParams: hp }); break;
    case 'addChildFolder': triggerAddChildFolderFromIcon(); break;
    case 'clone': emit('cardCloned', props.element); break;
    case 'addChildList': emit('addChildList', { hoverParams: hp }); break;
    case 'addToReport': emit('addToReport', props.element); break;
  }
}

function menuProjection(prType: number) {
  clearActionsSubCloseTimer();
  clearProjSubCloseTimer();
  menuOpen.value = false;
  // TODO: isExpanded пишется в проп. Перевести на событие наверх или на стор дерева.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isExpanded = true;
  emit('addChildProjection', { hoverParams: hoverParams(), pr_type: prType });
}

function menuChart(chartType: number) {
  clearActionsSubCloseTimer();
  clearProjSubCloseTimer();
  menuOpen.value = false;
  // TODO: isExpanded пишется в проп. Перевести на событие наверх или на стор дерева.
  // eslint-disable-next-line vue/no-mutating-props
  props.element.isExpanded = true;
  emit('addChildChart', { hoverParams: { ...hoverParams(), chartType } });
}

/**
 * «Добавить разрыв после» — отчётный экшен. Бэк ожидает (ReportId, BreakPosition):
 *   ReportId — Id родительского отчёта, BreakPosition — позиция в его ChildReportElements.
 * Текущий элемент — дитя REPORT-корня, поэтому parentId/parentType содержат identity отчёта.
 * newpos = sibling-index (по renameSiblings) + 1.
 */
function addBreakAfter() {
  if (!canAddBreak.value) return;
  if (props.parentType !== BaseItemTypes.REPORT || props.parentId == null) return;
  const siblings = props.renameSiblings || [];
  const idx = siblings.indexOf(props.element);
  if (idx < 0) return;
  emit('breakAfter', { Id: props.parentId, newpos: idx + 1 });
}
</script>

<style lang="scss" scoped>
.ble-lite {
  cursor: pointer;
  font-size: $body-font-size;
  color: $dark;
  min-height: 24px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  max-width: 100%;
  border: 1px solid transparent;
  transition: background-color 90ms ease-out, border-color 90ms ease-out;

  &--selected {
    border-color: #D2D2D2;
    border-radius: 8px;
  }

  // Одноразовая подсветка элемента при переходе по ссылке: только фон, без рамки.
  &--link-highlight {
    background-color: #F3F8F4;
    border-radius: 8px;
  }

  &--expanded {
    border-radius: 8px;
  }

  &--blocked {
    cursor: default;
    opacity: 0.72;
    pointer-events: none;
  }

  &--blocked &__name,
  &--blocked &__name :deep(.native-input),
  &--blocked &__name :deep(.native-input *) {
    color: #979797 !important;
  }

  &:hover {
    background-color: #F2F2F2;
    border-radius: 8px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    width: 100%;
    min-height: 24px;
    padding: 2px 4px 1px 0;
    box-sizing: border-box;
  }

  &__selection-check {
    margin-left: 8px;
    flex-shrink: 0;
    // Опускаем чекбокс выбора на 2px для визуальной центровки по высоте строки.
    position: relative;
    top: 2px;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    min-width: 16px;
    height: 20px;
    margin-left: 8px;
    margin-right: 5px;
    cursor: pointer;
    color: #999999;

    &:hover {
      color: #3F884F;
    }
  }

  &__chevron--open {
    color: #3F884F;
  }

  &__no-chevron {
    width: 8.5px;
    min-width: 8.5px;
    flex-shrink: 0;
  }

  &__no-chevron--full {
    /* Резервируем то же место, что и под иконку раскрытия (включая отступы) для папок и корневых элементов. */
    display: inline-flex;
    width: 16px;
    min-width: 16px;
    height: 20px;
    margin-left: 8px;
    margin-right: 5px;
    flex-shrink: 0;
  }

  &__chevron-icon {
    width: 16px;
    height: 16px;
    transition: transform 0.12s ease;
  }

  &__chevron:not(&__chevron--open) &__chevron-icon {
    transform: rotate(-90deg);
  }

  &__type-icon {
    font-size: 18px;
    width: 18px;
    min-width: 18px;
    height: 18px;
    color: #546e7a;
    position: relative;
    top: -2px;
    margin-right: 4px;
    margin-top: 2px;
    user-select: none;
    flex-shrink: 0;
  }

  &__type-icon--folder {
    top: -1px;
  }

  &__type-icon--list {
    top: -1px;
  }

  &__type-icon--line {
    top: 0.5px;
  }

  &__name {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: baseline;
    line-height: 18px;
    // Опускаем имя (NativeInput + projLink + edit-btn) на 2px вниз
    // для визуальной центровки по высоте строки .ble-lite__row (24px).
    // position: relative — чтобы не увеличивать min-height строки.
    // Относительный сдвиг 1.5px между edit/non-edit задаётся внутри NativeInput
    // (.native-input--editing .inner-editable { top: 1.5px }) и работает поверх этого.
    position: relative;
    top: 2px;
  }

  &__name-flow {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    display: inline-block;
    vertical-align: baseline;
    white-space: normal;
  }

  &__name-edit {
    display: inline-block;
    vertical-align: baseline;
    min-width: 0;
    max-width: 100%;
  }

  &__name-flow--editing {
    :deep(.native-input--editing .inner-editable) {
      top: 0;
      cursor: text;
    }
  }

  &__proj-link {
    display: inline;
    white-space: normal;
    overflow-wrap: anywhere;
    color: rgb(81, 81, 173);
    cursor: pointer;
  }

  &__edit-btn-tail {
    white-space: nowrap;
  }

  &__edit-btn {
    width: 15px;
    height: 15px;
    cursor: pointer;
    margin-left: 5px;
    display: inline-block;
    vertical-align: -3px;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &__edit-btn-icon {
    width: 100%;
    height: 100%;
    display: block;
  }

  &--actions-visible &__edit-btn,
  &--menu-open &__edit-btn {
    visibility: visible;
    opacity: 1;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
    padding-left: 4px;
    white-space: nowrap;
    flex-shrink: 0;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &--actions-visible &__right,
  &--menu-open &__right {
    visibility: visible;
    opacity: 1;
  }

  &__act {
    font-size: 16px;
    color: $accent;
    cursor: pointer;
    padding: 2px;
    border-radius: 2px;
    user-select: none;

    &:hover {
      background-color: #e0e0e0;
    }
  }

  &__heart-btn {
    display: inline-flex;
    align-items: flex-start;
    flex-shrink: 0;
    cursor: pointer;
    user-select: none;
  }

  &__heart-indicator {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    margin-left: 4px;
    user-select: none;
    pointer-events: none;
    margin-top: 3px;
  }

  &__act-menu {
    width: 16px;
    height: 16px;
    padding: 0;
    position: relative;
    top: 2px;

    &:hover {
      background-color: transparent;
    }
  }

  &__act-break {
    font-size: 16px;
    width: 16px;
    height: 16px;
    line-height: 16px;
    padding: 0;
    color: $accent;
    position: relative;
    top: 2px;
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #e0e0e0;
    }
  }

}
</style>

<style lang="scss">
.ble-lite__chevron-tooltip-popup {
  white-space: nowrap;
}
</style>
